import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { colors, fontMono, flagImgUrl, s } from "../lib/theme";
import { useIsMobile } from "../lib/useIsMobile";

export default function Stats() {
  const { activityId } = useParams();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [excludedQsos, setExcludedQsos] = useState(null);
  const [exchangeGaps, setExchangeGaps] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => {
    api.getStats(activityId).then(setData).catch((err) => setError(err.message));
    // Solo el organizador tiene acceso a esto — si no lo es, el 403 se
    // ignora en silencio y sencillamente no se muestra el panel.
    api.listExcludedQsos(activityId).then(setExcludedQsos).catch(() => {});
    api.getExchangeGaps(activityId).then(setExchangeGaps).catch(() => {});
  }, [activityId]);

  if (error) {
    return (
      <div style={s.page}>
        <div style={s.errorBox}>{error}</div>
        <Link to={`/actividades/${activityId}/matriz`} style={{ color: colors.amber }}>← Volver a la matriz</Link>
      </div>
    );
  }
  if (!data) return <div style={s.page}><p style={{ color: colors.textDim }}>Cargando…</p></div>;

  const maxByBand = Math.max(1, ...data.byBand.map((b) => b.count));
  const maxScore = Math.max(1, ...data.ranking.map((r) => r.score));
  const maxParticipantScore = Math.max(1, ...(data.participantRanking || []).map((r) => r.score));

  return (
    <div style={s.page}>
      <Link to={`/actividades/${activityId}/matriz`} style={{ display: "inline-flex", gap: 6, color: colors.textDim, fontWeight: 700, fontSize: 14, textDecoration: "none", marginBottom: 16 }}>
        ← Volver a la matriz
      </Link>

      <h1 style={s.h1}>Estadísticas</h1>
      <p style={s.subtitle}>Puntuación según la configuración de esta actividad.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, margin: "20px 0" }}>
        <KpiCard label="QSO TOTALES" value={data.total} color={colors.green} />
        <KpiCard label="OPERADORES CON QSO" value={data.ranking.length} color={colors.amber} />
        <KpiCard label="BANDAS USADAS" value={data.byBand.length} color={colors.text} />
        <KpiCard label="MODOS USADOS" value={data.byMode.length} color={colors.text} />
        <KpiCard label="ENTIDADES DXCC" value={data.entities?.length || 0} color={colors.amber} />
        {data.qslDownloads && (
          <KpiCard
            label="QSL DESCARGADAS"
            value={data.qslDownloads.mode === "summary"
              ? data.qslDownloads.downloaded
              : `${data.qslDownloads.downloaded} / ${data.qslDownloads.total}`}
            color={colors.amber}
          />
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={s.card}>
          <div style={s.eyebrow}>QSO POR BANDA</div>
          {data.byBand.length === 0 && <Empty />}
          {data.byBand.map((b) => (
            <BarRow key={b.band} label={b.band} value={b.count} max={maxByBand} />
          ))}
        </div>
        <div style={s.card}>
          <div style={s.eyebrow}>QSO POR MODO</div>
          {data.byMode.length === 0 && <Empty />}
          {data.byMode.map((m) => (
            <BarRow key={m.mode} label={m.mode} value={m.count} max={maxByBand} color={colors.amber} />
          ))}
        </div>
      </div>

      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.eyebrow}>ENTIDADES DXCC</div>
        <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: -6, marginBottom: 10 }}>
          La referencia real del hobby para contar "países trabajados" — mantenida por la ARRL. Un mismo país puede
          tener varias entidades distintas (p. ej. Canarias, Baleares y Ceuta/Melilla cuentan aparte de la España
          peninsular, aunque compartan bandera).
        </p>
        {(!data.entities || data.entities.length === 0) && <Empty />}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {data.entities?.map((e) => (
            <div key={e.entity} style={{
              display: "flex", alignItems: "center", gap: 8, background: colors.panelRaised,
              border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "6px 12px",
            }}>
              <img src={flagImgUrl(e.isoCode)} alt="" width={22} style={{ borderRadius: 2, display: "block" }} />
              <span style={{ fontSize: 13, color: colors.text }}>{e.entity}</span>
              <span style={{ fontFamily: fontMono, fontSize: 12, color: colors.textFaint }}>({e.count})</span>
            </div>
          ))}
        </div>
        {data.unresolvedCallsigns?.length > 0 && (
          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 12, color: colors.textFaint, cursor: "pointer" }}>
              {data.unresolvedCallsigns.length} indicativo{data.unresolvedCallsigns.length === 1 ? "" : "s"} sin reconocer — ver cuáles
            </summary>
            <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: 8, marginBottom: 8 }}>
              Su prefijo no está en nuestra tabla todavía. Pásamelos y los añado.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {data.unresolvedCallsigns.map((c) => (
                <span key={c} style={{
                  fontFamily: fontMono, fontSize: 11.5, color: colors.textDim, background: colors.panel,
                  border: `1px solid ${colors.lineStrong}`, borderRadius: 4, padding: "3px 7px",
                }}>{c}</span>
              ))}
            </div>
          </details>
        )}
      </div>

      {(excludedQsos?.length > 0 || exchangeGaps?.some((g) => g.gaps.length > 0)) && (
        <div style={{ ...s.card, marginBottom: 16 }}>
          <div style={s.eyebrow}>NÚMERO DE EXCHANGE — REVISIÓN</div>

          {exchangeGaps?.map((g) => (
            <div key={g.scope} style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 12.5, color: colors.text, margin: "0 0 4px" }}>
                {scopeLabel(g.scope)} — hasta el nº {g.maxNumber}
              </p>
              {g.gaps.length > 0 && (
                <p style={{ fontSize: 12, color: "#ffb4b4", margin: "0 0 4px" }}>
                  ⚠ Huecos sin explicar: {g.gaps.map((n) => String(n).padStart(3, "0")).join(", ")}
                </p>
              )}
              {g.excludedNumbers.length > 0 && (
                <p style={{ fontSize: 12, color: colors.textFaint, margin: 0 }}>
                  Números de QSOs excluidos (explican huecos): {g.excludedNumbers.map((n) => String(n).padStart(3, "0")).join(", ")}
                </p>
              )}
            </div>
          ))}

          {excludedQsos?.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ fontSize: 12, color: colors.textFaint, cursor: "pointer" }}>
                {excludedQsos.length} QSO{excludedQsos.length === 1 ? "" : "s"} excluido{excludedQsos.length === 1 ? "" : "s"} — ver detalle
              </summary>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                {excludedQsos.map((q) => (
                  <div key={q.id} style={{
                    fontSize: 12, color: colors.textDim, background: colors.panel,
                    border: `1px solid ${colors.lineStrong}`, borderRadius: 4, padding: "6px 10px",
                    display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
                  }}>
                    <span style={{ fontFamily: fontMono }}>
                      {q.callsign} · {q.band} · {q.mode}{q.exchange_number ? ` · nº ${String(q.exchange_number).padStart(3, "0")}` : ""}
                    </span>
                    <span>
                      registrado por {q.operator_callsign}, excluido por {q.excluded_by_callsign || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div style={{ ...s.card, marginBottom: 16 }}>
        <div style={s.eyebrow}>CLASIFICACIÓN DE PARTICIPANTES (LA DEL CONCURSO)</div>
        <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: -6, marginBottom: 10 }}>
          Puntuación de quienes han sido contactados — no de tu equipo de operadores.
          {data.participantRanking?.length > 50 && ` ${data.participantRanking.length} en total — con scroll para verlos todos.`}
        </p>
        {(!data.participantRanking || data.participantRanking.length === 0) && <Empty />}
        <div style={data.participantRanking?.length > 50 ? { maxHeight: 600, overflowY: "auto", paddingRight: 6 } : undefined}>
          {data.participantRanking?.map((r, i) => (
            <ParticipantRow key={r.callsign} r={r} i={i} maxParticipantScore={maxParticipantScore} />
          ))}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.eyebrow}>RANKING DE OPERADORES (USO INTERNO)</div>
        {data.ranking.length === 0 && <Empty />}
        {data.ranking.map((r, i) => (
          <div key={r.callsign} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ width: 20, fontSize: 13, fontWeight: 700, color: colors.textFaint }}>{i + 1}</span>
            <span style={{ width: 90, fontFamily: fontMono, fontSize: 13.5, fontWeight: 700, color: colors.text }}>{r.callsign}</span>
            <div style={{ flex: 1, background: colors.panelRaised, borderRadius: 4, overflow: "hidden", height: 18 }}>
              <div style={{ width: `${(r.score / maxScore) * 100}%`, background: i === 0 ? colors.green : "#2f6b45", height: "100%" }} />
            </div>
            <span style={{ width: 70, textAlign: "right", fontFamily: fontMono, fontSize: 13, color: colors.textDim }}>{r.score} pts</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div style={s.card}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", color: colors.textFaint, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: fontMono, fontSize: 28, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
function ParticipantRow({ r, i, maxParticipantScore }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <span style={{ width: 24, fontSize: 13, fontWeight: 700, color: colors.textFaint }}>{i + 1}</span>
      <span style={{ width: 90, fontFamily: fontMono, fontSize: 13.5, fontWeight: 700, color: colors.text }}>{r.callsign}</span>
      <div style={{ flex: 1, background: colors.panelRaised, borderRadius: 4, overflow: "hidden", height: 18 }}>
        <div style={{ width: `${(r.score / maxParticipantScore) * 100}%`, background: i === 0 ? colors.amber : "#8a6415", height: "100%" }} />
      </div>
      <span style={{ width: 70, textAlign: "right", fontFamily: fontMono, fontSize: 13, color: colors.textDim }}>{r.score} pts</span>
    </div>
  );
}

function BarRow({ label, value, max, color = colors.green }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <span style={{ width: 50, fontFamily: fontMono, fontSize: 12, color: colors.textDim }}>{label}</span>
      <div style={{ flex: 1, background: colors.panelRaised, borderRadius: 4, overflow: "hidden", height: 14 }}>
        <div style={{ width: `${(value / max) * 100}%`, background: color, height: "100%" }} />
      </div>
      <span style={{ width: 30, textAlign: "right", fontFamily: fontMono, fontSize: 12, color: colors.textDim }}>{value}</span>
    </div>
  );
}
function scopeLabel(scopeKey) {
  if (scopeKey === "general") return "General (toda la actividad)";
  const [type, ...rest] = scopeKey.split(":");
  if (type === "band") return `Banda ${rest[0]}`;
  if (type === "mode") return `Modo ${rest[0]}`;
  if (type === "band_mode") return `${rest[0]} · ${rest[1]}`;
  return scopeKey;
}

function Empty() {
  return <p style={{ fontSize: 13, color: colors.textFaint, margin: 0 }}>Todavía no hay QSOs registrados.</p>;
}
